// src/components/AttendanceFlow/AttendanceFlow.js - Tam Versiyon
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { OrbitLoaderComponent } from "../../utils/orbitLoader";
import { authFetch } from "../../services/authService";
import { getEmployeeTodayMovements } from "../../services/apiService";
import LoadingButton from "../LoadingButton/LoadingButton";
import "./AttendanceFlow.css";

const AttendanceFlow = ({ userId, onError }) => {
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [todayStats, setTodayStats] = useState({
    firstEntry: null,
    lastExit: null,
    totalWorkTime: 0,
    breakTime: 0,
  });
  const [rfidTag, setRfidTag] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date().toDateString());
  const navigate = useNavigate();
  const API_BASE_URL = "https://alternative-lamprey-fatma-celik-a9c7fc96.koyeb.app";

  useEffect(() => {
    const checkDayChange = setInterval(() => {
      const today = new Date().toDateString();
      if (today !== currentDate) {
        setCurrentDate(today);
        setAttendanceRecords([]);
        setTodayStats({
          firstEntry: null,
          lastExit: null,
          totalWorkTime: 0,
          breakTime: 0,
        });
        fetchAttendanceData();
      }
    }, 60000);
    return () => clearInterval(checkDayChange);
  }, [currentDate]);

  useEffect(() => {
    const fetchUserRfid = async () => {
      try {
        const response = await authFetch(
          `${API_BASE_URL}/api/employees/${userId}/personal-info`
        );
        if (response.ok) {
          const userData = await response.json();
          setRfidTag(userData.rfid_tag);
        } else {
          console.error("Kullanıcı bilgileri alınamadı");
          if (onError) onError("Kullanıcı bilgileri alınamadı");
        }
      } catch (error) {
        console.error("RFID etiketi çekme hatası:", error);
        if (onError) onError("RFID etiketi çekme hatası: " + error.message);
      }
    };
    if (userId) {
      fetchUserRfid();
    }
  }, [userId, onError]);

  const fetchAttendanceData = async () => {
    try {
      if (!refreshing) setLoading(true);
      const user = JSON.parse(localStorage.getItem("user"));
      const isAdmin =
        user.role === "Admin" || user.role_id === 1 || user.isAdmin;
      const endpoint = isAdmin
        ? `${API_BASE_URL}/api/recent-movements`
        : `${API_BASE_URL}/api/employees/${userId}/today-movements`;
      const response = await authFetch(endpoint);
      if (response.ok) {
        const data = await response.json();
        const formattedRecords = data.map((record) => ({
          type: record.access_type === "IN" ? "entry" : "exit",
          timestamp: record.access_time,
          location: "Şube",
          device: "Kart Okuyucu",
          note: record.note,
        }));
        const sortedRecords = [...formattedRecords].sort(
          (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
        );
        setAttendanceRecords(sortedRecords);
        const entryRecords = sortedRecords.filter((r) => r.type === "entry");
        const exitRecords = sortedRecords.filter((r) => r.type === "exit");
        const firstEntry =
          entryRecords.length > 0 ? entryRecords[0].timestamp : null;
        const lastExit =
          exitRecords.length > 0 ? exitRecords[0].timestamp : null;
        let totalWorkTime = 0;
        if (firstEntry && lastExit) {
          const entryTime = new Date(firstEntry);
          const exitTime = new Date(lastExit);
          totalWorkTime = Math.floor((exitTime - entryTime) / (1000 * 60));
        }
        setTodayStats({
          firstEntry,
          lastExit,
          totalWorkTime,
          breakTime: 0,
        });
      } else {
        console.error("Giriş/çıkış kayıtları alınamadı");
        if (onError) onError("Giriş/çıkış kayıtları alınamadı");
        setAttendanceRecords([]);
      }
      setLoading(false);
      setRefreshing(false);
    } catch (error) {
      console.error("Giriş/çıkış verileri çekme hatası:", error);
      if (onError)
        onError("Giriş/çıkış verileri çekme hatası: " + error.message);
      setAttendanceRecords([]);
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchAttendanceData();
    } else {
      setLoading(false);
    }
  }, [userId, onError]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchAttendanceData();
  };

  const handleAttendanceAction = async (actionType) => {
    if (!rfidTag) {
      alert("RFID etiketi bulunamadı. Lütfen sayfayı yenileyin.");
      return;
    }
    try {
      const response = await authFetch(`${API_BASE_URL}/api/access-logs`, {
        method: "POST",
        body: JSON.stringify({
          rfid_tag: rfidTag,
          user_id: userId,
          access_type: actionType === "entry" ? "IN" : "OUT",
        }),
      });
      if (response.ok) {
        handleRefresh();
      } else {
        const errorData = await response.json();
        console.error(`${actionType} işlemi başarısız oldu:`, errorData);
        alert(
          `${
            actionType === "entry" ? "Giriş" : "Çıkış"
          } işlemi sırasında bir hata oluştu: ${
            errorData.error || "Bilinmeyen hata"
          }`
        );
      }
    } catch (error) {
      console.error("İşlem hatası:", error);
      alert("İşlem sırasında bir hata oluştu: " + error.message);
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleTimeString("tr-TR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDuration = (minutes) => {
    if (!minutes || minutes === 0) return "0 saat";
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours} saat ${mins} dakika`;
  };

  if (loading) {
    return (
      <div className="attendance-loading">
        <OrbitLoaderComponent size="medium" showPath={true} />
        <p className="loading-text">Giriş/Çıkış verileri yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="attendance-flow">
      <div className="stats-grid">
        <div className="stat-card stat-primary">
          <div className="stat-icon">
            <i className="fas fa-sign-in-alt"></i>
          </div>
          <div className="stat-content">
            <span className="stat-label">İlk Giriş</span>
            <span className="stat-value">
              {formatTime(todayStats.firstEntry)}
            </span>
          </div>
        </div>
        <div className="stat-card stat-warning">
          <div className="stat-icon">
            <i className="fas fa-sign-out-alt"></i>
          </div>
          <div className="stat-content">
            <span className="stat-label">Son Çıkış</span>
            <span className="stat-value">
              {formatTime(todayStats.lastExit)}
            </span>
          </div>
        </div>
        <div className="stat-card stat-success">
          <div className="stat-icon">
            <i className="fas fa-business-time"></i>
          </div>
          <div className="stat-content">
            <span className="stat-label">Toplam Çalışma</span>
            <span className="stat-value">
              {formatDuration(todayStats.totalWorkTime)}
            </span>
          </div>
        </div>
        <div className="stat-card stat-info">
          <div className="stat-icon">
            <i className="fas fa-coffee"></i>
          </div>
          <div className="stat-content">
            <span className="stat-label">Mola Süresi</span>
            <span className="stat-value">
              {formatDuration(todayStats.breakTime)}
            </span>
          </div>
        </div>
      </div>
      <div className="attendance-timeline">
        <div className="timeline-header">
          <h3>
            <i className="fas fa-history"></i>
            Bugünkü Hareketleriniz
          </h3>
          <LoadingButton
            className={`refresh-button ${refreshing ? "spinning" : ""}`}
            onClick={handleRefresh}
            disabled={refreshing}
            title="Yenile"
            isLoading={refreshing}
            useSimpleSpinner={true}
          >
            <i className="fas fa-sync-alt"></i>
          </LoadingButton>
        </div>
        {attendanceRecords.length > 0 ? (
          <div className="timeline-container">
            {attendanceRecords.map((record, index) => (
              <div key={index} className="timeline-item">
                <div className={`timeline-marker ${record.type}`}>
                  <i
                    className={`fas fa-${
                      record.type === "entry" ? "sign-in-alt" : "sign-out-alt"
                    }`}
                  ></i>
                </div>
                <div className={`timeline-content ${record.type}`}>
                  <div className="timeline-header">
                    <h4>
                      {record.type === "entry" ? "İşe Giriş" : "İşten Çıkış"}
                    </h4>
                    <span className="timeline-time">
                      {formatTime(record.timestamp)}
                    </span>
                  </div>
                  <div className="timeline-details">
                    <div className="detail-item">
                      <i className="fas fa-map-marker-alt"></i>
                      <span>{record.location || "Şube"}</span>
                    </div>
                    <div className="detail-item">
                      <i className="fas fa-laptop"></i>
                      <span>{record.device || "Bilgisayar"}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-records">
            <i className="fas fa-calendar-times"></i>
            <p>Bugüne ait giriş/çıkış kaydı bulunamadı.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AttendanceFlow;
